import type { IValidationError, TFunction } from '@wyz/types';
import { uniq, uniqBy } from 'lodash-es';
import { DISCOUNT_TYPE } from '~/common/translators';
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';
import { DEFAULT_VEHICLE_TYPE } from '~/purchase/helpers';
import { ITranslatedVehicleTypeGroup, TranslationKeys } from '~/common/types';
import {
  IAdvancedExtraBonusDraft,
  IDistributionExtraBonusDraft,
  IDistributionExtraBonusVariant,
  IDistributionPricingPolicyBrand,
  IDistributionPricingPolicySeason,
  IPricingConfigurationType,
  IVehicleTypeColumn,
  IVehicleTypeOption,
  ISaveAdvancedExtraBonusesPayload,
  ISaveDistributionPricingPolicyBrandPayload,
} from '~/purchase/types';

const DEFAULT_CURRENCY_SIGN = '€';

export const NO_VALUE = '-';

/**
 * Catalog season ids are stable, so they drive the colour band. An unknown id simply gets no tint.
 */
const SEASON_MODIFIERS: Record<number, string> = {
  1: 'summer',
  2: 'winter',
  3: 'all-season',
};

export function seasonClass(season: IDistributionPricingPolicySeason): string {
  const modifier = SEASON_MODIFIERS[season.id];

  return modifier ? ` extra-bonus-table__season--${modifier}` : '';
}

export function tintClass(season: IDistributionPricingPolicySeason): string {
  const modifier = SEASON_MODIFIERS[season.id];

  return modifier ? ` extra-bonus-table__tint--${modifier}` : '';
}

export function tooltipClass(season: IDistributionPricingPolicySeason): string {
  const modifier = SEASON_MODIFIERS[season.id];

  return modifier ? ` extra-bonus-tooltip--${modifier}` : '';
}

export const EMPTY_DRAFT: IDistributionExtraBonusDraft = {
  bonusType: PRICING_TYPE_ENUM.DISCOUNT_BF,
  hqBonus: null,
  values: {},
};

/**
 * The total is only derivable for a BF discount, on both sides: the purchase amount has to be a BF
 * discount for the percentage to apply to it, and the bonuses themselves have to be Remise BF — a
 * margin or a net margin expresses something the column cannot show. The HQ bonus and the extra bonus
 * carry their own type, so each one is checked against its own.
 *
 * The two bonuses stack, so the discount applied to the purchase amount is their sum. Either one alone
 * is enough for a total; only a cell without any bonus at all has none.
 */
export function computeTotal(
  purchaseValue: number,
  discountType: number,
  extraBonus: number | null,
  extraBonusType: IPricingConfigurationType | null,
  hqBonus: number | null = null,
  hqBonusType: IPricingConfigurationType | null = null,
): number | null {
  const isBfDiscount = (
    bonus: number | null,
    bonusType: IPricingConfigurationType | null,
  ) => null === bonus || PRICING_TYPE_ENUM.DISCOUNT_BF === bonusType;

  if (
    discountType !== DISCOUNT_TYPE.BF ||
    (null === extraBonus && null === hqBonus) ||
    !isBfDiscount(extraBonus, extraBonusType) ||
    !isBfDiscount(hqBonus, hqBonusType)
  ) {
    return null;
  }

  const ratio = ((hqBonus ?? 0) + (extraBonus ?? 0)) / 100;

  return purchaseValue * (1 - ratio);
}

/** Extra bonuses are keyed on a supplier type and a season, whatever the diameters behind them. */
export function cellKey(supplierTypeId: number, seasonId: number): string {
  return `${supplierTypeId}-${seasonId}`;
}

/** A discount and a margin are percentages; a net margin is an amount in the value's currency. */
export function bonusSign(bonusType?: IPricingConfigurationType): string {
  return PRICING_TYPE_ENUM.NET_MARGIN === bonusType
    ? DEFAULT_CURRENCY_SIGN
    : '%';
}

/** The column a value belongs to when the vehicle types are not split apart. */
export const EVERY_VEHICLE_TYPE = 'ALL';

/**
 * The vehicle types a bonus may target: the ones of the default group, as every pricing policy screen
 * reads them. A catalog entry already carries the four fields an Option holds, so a column posts what
 * it shows.
 */
export function advancedVehicleTypeColumns(
  groups: ITranslatedVehicleTypeGroup[],
  t: TFunction<TranslationKeys>,
): IVehicleTypeColumn[] {
  return (
    groups
      .find((group) => group.name === DEFAULT_VEHICLE_TYPE)
      ?.vehicleTypes.map((vehicleType) => ({
        value: vehicleType.publicKey,
        label: t(vehicleType.i18nKey, { defaultValue: vehicleType.name }),
        option: {
          id: vehicleType.externalId,
          key: vehicleType.publicKey,
          name: vehicleType.name,
          i18nKey: vehicleType.i18nKey,
        },
      })) ?? []
  );
}

export const EMPTY_ADVANCED_DRAFT: IAdvancedExtraBonusDraft = {
  brands: [],
  supplierTypeIds: [],
  hqBonusType: PRICING_TYPE_ENUM.DISCOUNT_BF,
  hqBonus: null,
  bonusType: PRICING_TYPE_ENUM.DISCOUNT_BF,
  byVehicleType: false,
  byDiameter: false,
  diameters: [],
  values: {},
};

/**
 * A cell of the advanced matrix is a season, a vehicle type and a diameter row. The vehicle type and
 * the row collapse to a single one when their axis is switched off, so the key stays the same shape
 * whatever the axes the bonus was entered with.
 */
export function advancedCellKey(
  seasonId: number,
  vehicleType: string,
  rowIndex: number,
): string {
  return `${seasonId}-${vehicleType}-${rowIndex}`;
}

/** The diameter rows the matrix carries: a single, unrestricted one when the axis is off. */
export function advancedDiameterRows(
  draft: IAdvancedExtraBonusDraft,
): number[] {
  const count = draft.byDiameter ? (draft.diameters?.length ?? 0) : 1;

  return Array.from({ length: count }, (_, index) => index);
}

/** The vehicle type columns the matrix carries: a single, unrestricted one when the axis is off. */
export function advancedVisibleColumns(
  draft: IAdvancedExtraBonusDraft,
  columns: IVehicleTypeColumn[],
): IVehicleTypeColumn[] {
  return draft.byVehicleType
    ? columns
    : [
        {
          value: EVERY_VEHICLE_TYPE,
          label: '',
          option: EVERY_VEHICLE_TYPE_OPTION,
        },
      ];
}

/**
 * The sources the selected brands all carry: a bonus is written for every brand of the selection and
 * only where a line exists, so a source one of them lacks could never be saved.
 */
export function carriedSupplierTypeIds(
  brands: IDistributionPricingPolicyBrand[],
  selectedBrands: string[],
): number[] {
  const selected = brands.filter((brand) =>
    selectedBrands.includes(brand.brand),
  );

  if (0 === selected.length) {
    return [];
  }

  return uniq(selected[0].rows.map((row) => row.supplierTypeId)).filter(
    (supplierTypeId) =>
      selected.every((brand) =>
        brand.rows.some((row) => row.supplierTypeId === supplierTypeId),
      ),
  );
}

/**
 * Names every cell the matrix is about to render, defaulting to null the ones the draft does not fill.
 *
 * The amount inputs bind to `useWatch`, and a key missing from `values` watches as `undefined`, which
 * React reads as "uncontrolled": the input then keeps whatever the previous entry left on screen. Only
 * a value that is present — null included — clears it. Every reset of the form goes through here.
 */
export function withEveryCell(
  draft: IAdvancedExtraBonusDraft,
  seasons: IDistributionPricingPolicySeason[],
  vehicleTypes: IVehicleTypeColumn[],
): IAdvancedExtraBonusDraft {
  const cells: IAdvancedExtraBonusDraft['values'] = {};
  const columns = advancedVisibleColumns(draft, vehicleTypes);

  advancedDiameterRows(draft).forEach((rowIndex) => {
    seasons.forEach((season) => {
      columns.forEach((column) => {
        cells[advancedCellKey(season.id, column.value, rowIndex)] = null;
      });
    });
  });

  return { ...draft, values: { ...cells, ...draft.values } };
}

/** Stands for "every vehicle type": the column posts no criterion at all. */
const EVERY_VEHICLE_TYPE_OPTION: IVehicleTypeOption = {
  id: 0,
  key: EVERY_VEHICLE_TYPE,
  name: '',
  i18nKey: '',
};

/**
 * A cleared cell is simply not sent: a bonus targets the criteria it was entered with, and an axis
 * left switched off is sent as "every value of that axis" rather than as one entry per value.
 */
export function toSaveAdvancedPayload(
  draft: IAdvancedExtraBonusDraft,
  seasons: IDistributionPricingPolicySeason[],
  columns: IVehicleTypeColumn[],
): ISaveAdvancedExtraBonusesPayload {
  return {
    brands: draft.brands,
    supplierTypeIds: draft.supplierTypeIds,
    hqBonusType: draft.hqBonusType,
    hqBonus: draft.hqBonus ?? null,
    bonusType: draft.bonusType,
    values: seasons.flatMap((season) =>
      advancedVisibleColumns(draft, columns).flatMap((column) =>
        advancedDiameterRows(draft).flatMap((rowIndex) => {
          const value =
            draft.values?.[advancedCellKey(season.id, column.value, rowIndex)];

          if (null === value || undefined === value || Number.isNaN(value)) {
            return [];
          }

          return [
            {
              seasonId: season.id,
              vehicleType:
                EVERY_VEHICLE_TYPE === column.value ? null : column.option,
              diameter: draft.byDiameter
                ? (draft.diameters?.[rowIndex]?.diameter ?? null)
                : null,
              value,
            },
          ];
        }),
      ),
    ),
  };
}

/**
 * The amounts are keyed by the row they were entered on, so removing a row has to move the ones below
 * it up: the row a diameter carries and the row an amount sits on would drift apart otherwise.
 */
export function removeAdvancedDiameterRow(
  values: IAdvancedExtraBonusDraft['values'],
  removedRowIndex: number,
): IAdvancedExtraBonusDraft['values'] {
  const shifted: IAdvancedExtraBonusDraft['values'] = {};

  Object.entries(values ?? {}).forEach(([key, value]) => {
    const separator = key.lastIndexOf('-');
    const rowIndex = Number(key.slice(separator + 1));

    if (rowIndex === removedRowIndex) {
      return;
    }

    const target = rowIndex > removedRowIndex ? rowIndex - 1 : rowIndex;

    shifted[`${key.slice(0, separator)}-${target}`] = value;
  });

  return shifted;
}

/** Two rules share a diameter row when they carry the very same range. */
function diameterRowKey(rule: IDistributionExtraBonusVariant): string {
  return `${rule.diameterOperator}-${rule.minDiameter}-${rule.maxDiameter}`;
}

/**
 * The reverse of `toSaveAdvancedPayload`: the bonus a brand holds, read back into the form the advanced
 * screen edits. The axes are switched on by what the rules actually target, and the diameter ranges
 * become the rows they were entered on, ordered by their lower bound.
 *
 * A rule covering every season — which this screen never writes, but older data may hold — fills every
 * season column, since that is what it means.
 */
export function toAdvancedDraft(
  brand: IDistributionPricingPolicyBrand,
  seasons: IDistributionPricingPolicySeason[],
): IAdvancedExtraBonusDraft {
  const rules = brand.extraBonusRules ?? [];
  const byDiameter = rules.some((rule) => null !== rule.diameterOperator);
  const diameterRules = byDiameter
    ? uniqBy(
        [...rules]
          .filter((rule) => null !== rule.diameterOperator)
          .sort(
            (left, right) => (left.minDiameter ?? 0) - (right.minDiameter ?? 0),
          ),
        diameterRowKey,
      )
    : [];
  const rowIndexOf = (rule: IDistributionExtraBonusVariant) => {
    const index = diameterRules.findIndex(
      (candidate) => diameterRowKey(candidate) === diameterRowKey(rule),
    );

    return -1 === index ? 0 : index;
  };

  const values: IAdvancedExtraBonusDraft['values'] = {};

  rules.forEach((rule) => {
    const seasonIds =
      null === rule.seasonId
        ? seasons.map((season) => season.id)
        : [rule.seasonId];

    seasonIds.forEach((seasonId) => {
      values[
        advancedCellKey(
          seasonId,
          rule.vehicleType?.key ?? EVERY_VEHICLE_TYPE,
          rowIndexOf(rule),
        )
      ] = rule.value;
    });
  });

  return {
    brands: [brand.brand],
    // Older bonuses have no stored sources, so they fall back to the rules they used to be read from.
    supplierTypeIds:
      0 === brand.supplierTypeIds.length
        ? uniq(
            rules.flatMap((rule) =>
              null === rule.supplierTypeId ? [] : [rule.supplierTypeId],
            ),
          )
        : brand.supplierTypeIds,
    hqBonusType: brand.hqBonusType ?? PRICING_TYPE_ENUM.DISCOUNT_BF,
    hqBonus: brand.hqBonus,
    bonusType: rules[0]?.bonusType ?? PRICING_TYPE_ENUM.DISCOUNT_BF,
    byVehicleType: rules.some((rule) => null !== rule.vehicleType),
    byDiameter,
    diameters: diameterRules.map((rule) => ({
      diameter: {
        operator: rule.diameterOperator as NonNullable<
          IDistributionExtraBonusVariant['diameterOperator']
        >,
        from: rule.minDiameter,
        to: rule.maxDiameter,
      },
    })),
    values,
  };
}

/** The criteria of an advanced bonus, as the tooltip of the grid lists them. */
export function variantDiameterLabel(
  variant: IDistributionExtraBonusVariant,
): string | null {
  if (null === variant.diameterOperator) {
    return null;
  }

  const { minDiameter, maxDiameter } = variant;

  switch (variant.diameterOperator) {
    case 'lte':
      return `≤ ${maxDiameter}`;
    case 'gte':
      return `≥ ${minDiameter}`;
    case 'between':
      return `${minDiameter} – ${maxDiameter}`;
    default:
      return String(minDiameter);
  }
}

export function toEditionDraft(
  brand: IDistributionPricingPolicyBrand,
): IDistributionExtraBonusDraft {
  const values: IDistributionExtraBonusDraft['values'] = {};

  brand.rows.forEach((row) => {
    Object.entries(row.seasons).forEach(([seasonId, cell]) => {
      values[cellKey(row.supplierTypeId, Number(seasonId))] =
        cell?.extraBonus ?? null;
    });
  });

  return {
    bonusType: brand.hqBonusType ?? PRICING_TYPE_ENUM.DISCOUNT_BF,
    hqBonus: brand.hqBonus,
    values,
  };
}

/**
 * A cleared cell is simply not sent, so the position of a cell in the payload is not the position of
 * its field in the draft. The kept entries are listed once here, and both the payload and the mapping
 * back from the server property paths read that same list.
 */
export function payloadEntries(
  draft: IDistributionExtraBonusDraft,
): [string, number][] {
  return Object.entries(draft.values ?? {}).filter(
    (entry): entry is [string, number] =>
      null !== entry[1] && undefined !== entry[1] && !Number.isNaN(entry[1]),
  );
}

/** A cleared cell is simply not sent: the whole brand travels in one save. */
export function toSaveBrandPayload(
  brand: string,
  draft: IDistributionExtraBonusDraft,
): ISaveDistributionPricingPolicyBrandPayload {
  return {
    brand,
    bonusType: draft.bonusType,
    hqBonus: draft.hqBonus ?? null,
    values: payloadEntries(draft).map(([key, value]) => {
      const [supplierTypeId, seasonId] = key.split('-');

      return {
        supplierTypeId: Number(supplierTypeId),
        seasonId: Number(seasonId),
        value,
      };
    }),
  };
}

const VALUE_PROPERTY_PATH = /^values\[(\d+)\](?:\.value)?$/;

/**
 * The server validates the payload, whose values are an array, while the fields are keyed on a
 * supplier type and a season. A violation on `values[0].value` is therefore moved back onto the field
 * that produced that entry. A path pointing at nothing on screen — the brand, or an index the draft no
 * longer holds — is dropped: the generic error toast already reports the failure.
 */
export function toExtraBonusFormErrors<TError extends IValidationError>(
  errors: TError[],
  draft: IDistributionExtraBonusDraft,
): TError[] {
  const keys = payloadEntries(draft).map(([key]) => key);

  return errors.reduce<TError[]>((mapped, error) => {
    if (
      'hqBonus' === error.propertyPath ||
      'bonusType' === error.propertyPath
    ) {
      mapped.push(error);

      return mapped;
    }

    const [, index] = VALUE_PROPERTY_PATH.exec(error.propertyPath) ?? [];
    const key = undefined === index ? undefined : keys[Number(index)];

    if (undefined !== key) {
      mapped.push({ ...error, propertyPath: `values.${key}` });
    }

    return mapped;
  }, []);
}

/**
 * A divergent cell is summarised by the range its combinations span, which stays readable whatever
 * the number of diameters. The unit only belongs to the summary when they all share the same
 * discount type, so a divergence there returns no discount type at all.
 */
export function summarizeRange(
  values: number[],
  discountTypes: number[],
): { text: string; discountType: number | null } | null {
  if (0 === values.length) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const [discountType] = discountTypes;
  const isSameDiscountType = discountTypes.every(
    (type) => type === discountType,
  );

  return {
    text: min === max ? String(min) : `${min} – ${max}`,
    discountType: isSameDiscountType ? discountType : null,
  };
}
